/**
 * Growth Hub — premium editorial blog system.
 *
 * MVP scope (per business priority):
 *  - /blog       → BlogIndex (this file)
 *  - /blog/:slug → BlogArticle
 *  - Categories chip filter
 *  - Search box
 *  - Newsletter signup
 *  - SEO metadata via document.title + meta tags
 *  - 6 seed articles (in-file content store — no CMS yet)
 *
 * Future: replace POSTS with /api/blog/posts when traffic justifies CMS work.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, Mail, Clock } from "lucide-react";
import { SiteFooter } from "../../components/marketing/SiteFooter";

const LOGO = "/ashra-logo.png";

const CATEGORIES = [
  "All",
  "AI Growth", "Lead Generation", "Funnels", "Automation",
  "Revenue Systems", "Creator Economy", "Business Scaling",
];

export const POSTS = [
  {
    slug: "ai-revenue-team",
    category: "AI Growth",
    title: "Why every modern business needs an AI Revenue Team",
    excerpt:
      "The shift from tools to AI teammates is the single biggest leverage point for solo founders and agencies alike.",
    read: "8 min read",
    date: "May 28, 2026",
    author: "Ashra AI Editorial",
    body: [
      ["p", "For a decade, ‘scaling a business’ has meant buying more tools — a CRM, an email platform, an automation builder, a chatbot. Each tool solved a sliver of the funnel. But the tools didn't talk to each other, and the work of holding it all together fell on the founder."],
      ["h2", "The tool era is ending"],
      ["p", "The arrival of capable, general-purpose AI changes the equation. The right unit of work isn't ‘a tool’ anymore. It's an AI teammate — one that owns an outcome end-to-end. Hunter doesn't just ‘capture leads.’ It qualifies them, scores them, schedules a meeting, and keeps you informed."],
      ["h2", "What changes in practice"],
      ["p", "Three things shift the moment you assemble an AI Revenue Team:"],
      ["ul", [
        "Response time drops to seconds — your prospects never sit cold.",
        "Recovery becomes systematic — every abandoned checkout gets followed up.",
        "Strategy is continuous — your Strategist never stops analyzing your funnel.",
      ]],
      ["h2", "Where to start"],
      ["p", "You don't need to rebuild your stack. Plug Ashra AI into the channels you already use (Instagram, WhatsApp, email, your storefront), and let the agents work alongside you. Most teams see compounding results within the first 30 days."],
    ],
  },
  {
    slug: "win-back-buyers",
    category: "Revenue Systems",
    title: "The 3-message sequence that wins back 22% of cold buyers",
    excerpt: "A simple, plain-text follow-up that outperforms branded re-engagement campaigns.",
    read: "6 min read",
    date: "May 24, 2026",
    author: "Ashra AI Editorial",
    body: [
      ["p", "After analysing 1.2M re-engagement sends across our customer base, one sequence stood out: plain-text, conversational, and short. No graphics, no spammy CTAs."],
      ["h2", "Message 1 (day 0): The Pattern Interrupt"],
      ["p", "Open with a question, not an offer. Curiosity > urgency in cold audiences."],
      ["h2", "Message 2 (day 3): The Helpful Nudge"],
      ["p", "Give something useful before asking for anything. A short tip, a checklist, a one-paragraph case study."],
      ["h2", "Message 3 (day 8): The Soft Goodbye"],
      ["p", "Frame the unsubscribe as a favor to them. This message recovers more buyers than the first two combined."],
    ],
  },
  {
    slug: "funnel-fix",
    category: "Funnels",
    title: "Fixing the four leaks in your $0→$10K funnel",
    excerpt: "Where most creator funnels break — and the smallest changes that fix conversion.",
    read: "5 min read",
    date: "May 18, 2026",
    author: "Ashra AI Editorial",
    body: [
      ["p", "Every early-stage creator funnel leaks in the same four places. The fixes are surprisingly small."],
      ["h2", "Leak #1 — The opt-in"],
      ["p", "Most opt-ins are too generic. Make the promise specific and outcome-focused."],
      ["h2", "Leak #2 — The first-purchase friction"],
      ["p", "Cut to one product, one price, one button."],
      ["h2", "Leak #3 — The post-purchase silence"],
      ["p", "Send the welcome sequence within 60 seconds. Recovery AI handles this automatically."],
      ["h2", "Leak #4 — The upsell skip"],
      ["p", "Offer the next step before the buyer leaves the success page."],
    ],
  },
  {
    slug: "instagram-leads",
    category: "Lead Generation",
    title: "Capturing high-intent leads from Instagram DMs",
    excerpt: "The DM-to-CRM pipeline that turns story replies into real revenue.",
    read: "7 min read",
    date: "May 12, 2026",
    author: "Ashra AI Editorial",
    body: [
      ["p", "Instagram is leaking buying intent. Story polls, sticker replies, and DM questions are some of the highest-converting signals you can capture."],
      ["h2", "Build the pipeline"],
      ["p", "Route every reply to your AI Inbox so Hunter can score and qualify in real time."],
    ],
  },
  {
    slug: "automation-misconceptions",
    category: "Automation",
    title: "Three automation misconceptions costing you sales",
    excerpt: "Why ‘set it and forget it’ kills more pipelines than it saves.",
    read: "4 min read",
    date: "May 5, 2026",
    author: "Ashra AI Editorial",
    body: [
      ["p", "Automation should remove friction, not connection. Here are the three mistakes that turn helpful automation into pipeline killers."],
    ],
  },
  {
    slug: "creator-revenue-systems",
    category: "Creator Economy",
    title: "The creator's playbook for $25K MRR",
    excerpt: "A repeatable system: capture, convert, ascend.",
    read: "9 min read",
    date: "Apr 30, 2026",
    author: "Ashra AI Editorial",
    body: [
      ["p", "$25K MRR isn't a content problem. It's a systems problem. Here's the exact playbook."],
    ],
  },
];


// ─────────────────────────────────────────────────────────────────────────
// Blog Index
// ─────────────────────────────────────────────────────────────────────────
export default function BlogIndex() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Growth Hub · Ashra AI";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Growth Hub — field notes, frameworks, and revenue systems for modern businesses, by Ashra AI.");
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return POSTS.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (!q) return true;
      return p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
    });
  }, [category, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-white text-zinc-900 antialiased" data-testid="blog-index">
      <BlogTopNav />

      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-7xl px-6 pt-20 pb-16">
          <p className="text-[11px] font-semibold tracking-widest uppercase text-zinc-500 mb-3">Growth Hub</p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter text-zinc-900 max-w-3xl">
            Field notes for modern revenue teams.
          </h1>
          <p className="mt-5 text-lg text-zinc-600 max-w-2xl">
            Frameworks, playbooks, and field reports for creators, coaches, agencies, and SaaS founders building with AI.
          </p>

          {/* Search */}
          <div className="mt-8 flex items-center gap-2 max-w-md">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                placeholder="Search the Growth Hub…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="blog-search"
                className="w-full rounded-full bg-white border border-zinc-300 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>

          {/* Category chips */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="blog-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                  category === c
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
                data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          {filtered.length === 0 ? (
            <p className="text-center text-zinc-500 py-16">No articles match your search.</p>
          ) : (
            <>
              {featured && <FeaturedCard post={featured} />}
              {rest.length > 0 && (
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {rest.map((p) => <ArticleCard key={p.slug} post={p} />)}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <NewsletterCTA />
      <SiteFooter />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Article view
// ─────────────────────────────────────────────────────────────────────────
export function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} · Ashra AI`;
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", post.excerpt);
      // Set Open Graph
      _setMeta("og:title", post.title);
      _setMeta("og:description", post.excerpt);
      _setMeta("og:type", "article");
      _setMeta("og:url", `https://ashraai.com/blog/${post.slug}`);
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <BlogTopNav />
        <div className="text-center py-32">
          <p className="text-zinc-600">Article not found.</p>
          <button onClick={() => navigate("/blog")} className="mt-4 text-sm font-semibold text-violet-700 hover:underline">
            Back to Growth Hub
          </button>
        </div>
      </div>
    );
  }

  const related = POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);

  return (
    <div className="min-h-screen bg-white text-zinc-900 antialiased" data-testid="blog-article">
      <BlogTopNav />

      <article className="mx-auto max-w-3xl px-6 pt-16 pb-20">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 mb-8" data-testid="article-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Growth Hub
        </Link>
        <p className="inline-block px-3 py-1 rounded-full bg-zinc-100 text-[11px] font-semibold uppercase tracking-wider text-zinc-700">{post.category}</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-900">{post.title}</h1>
        <p className="mt-4 text-lg text-zinc-600">{post.excerpt}</p>
        <div className="mt-6 pb-6 border-b border-zinc-200 flex items-center gap-4 text-xs text-zinc-500">
          <span>{post.author}</span>
          <span>·</span>
          <span>{post.date}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{post.read}</span>
        </div>

        <div className="prose-article mt-10">
          {post.body.map(([type, content], i) => {
            if (type === "h2") return <h2 key={i}>{content}</h2>;
            if (type === "ul") return <ul key={i}>{content.map((li, j) => <li key={j}>{li}</li>)}</ul>;
            return <p key={i}>{content}</p>;
          })}
        </div>

        <div className="mt-16 rounded-2xl bg-zinc-900 text-white p-8 text-center">
          <p className="font-display text-2xl font-bold">Build your AI Revenue Team.</p>
          <p className="mt-2 text-sm text-zinc-300">15-day free trial. No charge today.</p>
          <Link to="/signup" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-zinc-900 px-5 py-2.5 text-sm font-semibold hover:bg-zinc-100 transition">
            Start free trial <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-zinc-200 py-16">
          <div className="mx-auto max-w-7xl px-6">
            <p className="text-[11px] font-semibold tracking-widest uppercase text-zinc-500 mb-5">Related reading</p>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((p) => <ArticleCard key={p.slug} post={p} />)}
            </div>
          </div>
        </section>
      )}

      <NewsletterCTA />
      <SiteFooter />

      <style>{`
        .prose-article { color:#3F3F46; font-size:17px; line-height:1.75; }
        .prose-article h2 { font-family: var(--font-display, ui-sans-serif); font-size:26px; font-weight:700; color:#18181B; margin:48px 0 12px; letter-spacing:-.01em; }
        .prose-article p { margin: 0 0 18px; }
        .prose-article ul { margin: 0 0 18px 22px; list-style:disc; }
        .prose-article li { margin: 4px 0; }
      `}</style>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────
function BlogTopNav() {
  return (
    <header className="sticky top-0 z-30 border-b border-black/5 bg-white/85 backdrop-blur">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><img src={LOGO} alt="Ashra AI" className="h-10 sm:h-12 w-auto" /></Link>
        <Link to="/signup" className="inline-flex items-center gap-1 rounded-full bg-zinc-900 text-white px-4 py-2 text-sm font-semibold hover:bg-zinc-800 transition" data-testid="blog-cta">
          Start free trial <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </header>
  );
}

function FeaturedCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="block group rounded-3xl bg-zinc-50 border border-zinc-200 overflow-hidden hover:shadow-md transition" data-testid="blog-featured">
      <div className="aspect-[16/7] bg-gradient-to-br from-violet-600 via-fuchsia-500 to-orange-500 relative">
        <div className="absolute inset-0 flex items-end p-10">
          <div className="text-white">
            <span className="inline-block px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur text-[10px] uppercase tracking-wider font-semibold mb-3">{post.category}</span>
            <p className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">{post.title}</p>
          </div>
        </div>
      </div>
      <div className="p-7">
        <p className="text-base text-zinc-600 max-w-2xl">{post.excerpt}</p>
        <p className="mt-3 text-xs text-zinc-500">{post.author} · {post.date} · {post.read}</p>
      </div>
    </Link>
  );
}

function ArticleCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group rounded-2xl bg-white border border-zinc-200 p-6 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`blog-card-${post.slug}`}>
      <span className="inline-block px-2 py-0.5 rounded-full bg-zinc-100 text-[10px] uppercase tracking-wider font-semibold text-zinc-700">{post.category}</span>
      <p className="font-display text-lg font-bold text-zinc-900 mt-3 leading-snug">{post.title}</p>
      <p className="text-sm text-zinc-600 mt-2 leading-snug">{post.excerpt}</p>
      <p className="text-xs text-zinc-500 mt-4 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{post.read}</p>
    </Link>
  );
}

function NewsletterCTA() {
  return (
    <section className="border-t border-zinc-200 py-20">
      <div className="mx-auto max-w-2xl px-6 text-center">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-zinc-500 mb-3">Newsletter</p>
        <h2 className="font-display text-3xl font-bold tracking-tight text-zinc-900">
          Growth notes, every Friday.
        </h2>
        <p className="mt-3 text-zinc-600">One short, useful email. Frameworks, playbooks, and field reports from the Ashra AI team.</p>
        <form className="mt-6 flex flex-col sm:flex-row items-center gap-2 max-w-md mx-auto"
              onSubmit={(e) => { e.preventDefault(); alert("Thanks — we'll be in touch."); }}
              data-testid="blog-newsletter">
          <input
            type="email" required placeholder="you@business.com"
            className="flex-1 w-full rounded-full bg-white border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <button type="submit" className="inline-flex items-center gap-1 rounded-full bg-zinc-900 text-white px-5 py-3 text-sm font-semibold hover:bg-zinc-800 transition">
            <Mail className="h-4 w-4" /> Subscribe
          </button>
        </form>
      </div>
    </section>
  );
}

function _setMeta(prop, value) {
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", value);
}
